import React from 'react';
import {
  FaEnvelope,
  FaPhone,
  FaCheckCircle,
  FaTimesCircle,
  FaTrash,
  FaSpinner,
  FaGraduationCap,
  FaBullseye,
  FaCalendarAlt,
  FaIdCard,
  FaMoneyBillWave,
  FaClock,
  FaTag,
} from 'react-icons/fa';
import { formatEnrolledAt } from '../../../utils/formatDate';
import {
  formatCourseFee,
  getAmountPaid,
  getDiscountAmount,
  getDiscountPercent,
  getEffectiveCourseFee,
  getPaymentStatus,
  getRemainingBalance,
  getTotalDiscountGiven,
  getTotalIncome,
  isFullyPaid,
  CREATIVE_SAMURAI_COURSE_FEE,
} from '../../../utils/paymentStatus';

const EnrolledAt = ({ dateString, textClass = 'text-sm text-gray-600' }) => {
  const { date, time } = formatEnrolledAt(dateString);
  return (
    <div>
      <p className={textClass}>{date}</p>
      <p className={`${textClass} text-gray-500`}>{time}</p>
    </div>
  );
};

const NameContact = ({ enrollment, nameClass = 'font-semibold text-designer-dark' }) => (
  <div className="min-w-[180px]">
    <p className={nameClass}>{enrollment.name}</p>
    <div className="mt-1.5 space-y-1">
      <a
        href={`mailto:${enrollment.email}`}
        className="flex items-center gap-1.5 text-designer-primary hover:underline min-w-0"
      >
        <FaEnvelope className="w-3 h-3 shrink-0" />
        <span className="text-xs truncate">{enrollment.email}</span>
      </a>
      <a
        href={`tel:${enrollment.phone}`}
        className="flex items-center gap-1.5 text-gray-500 hover:text-designer-dark"
      >
        <FaPhone className="w-3 h-3 shrink-0" />
        <span className="text-xs">{enrollment.phone}</span>
      </a>
    </div>
  </div>
);

const PaymentBadge = ({ enrollment, size = 'sm' }) => {
  const status = getPaymentStatus(enrollment);

  const config = {
    paid: {
      className: 'bg-green-100 text-green-700',
      icon: <FaCheckCircle className="w-3 h-3" />,
      label: 'Paid',
    },
    partial: {
      className: 'bg-blue-100 text-blue-700',
      icon: <FaClock className="w-3 h-3" />,
      label: 'Partial Paid',
    },
    unpaid: {
      className: 'bg-amber-100 text-amber-700',
      icon: <FaTimesCircle className="w-3 h-3" />,
      label: 'Unpaid',
    },
  };

  const { className, icon, label } = config[status];

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-semibold whitespace-nowrap ${
        size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1 text-sm'
      } ${className}`}
    >
      {icon}
      {label}
    </span>
  );
};

const DiscountBadge = ({ enrollment }) => {
  const discount = getDiscountAmount(enrollment);
  if (!discount) return null;

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 border border-green-200">
      <FaTag className="w-2.5 h-2.5" />
      -{getDiscountPercent(discount)}% ({formatCourseFee(discount)})
    </span>
  );
};

const PaidAmountDisplay = ({ enrollment, compact = false }) => {
  const amountPaid = getAmountPaid(enrollment);
  const status = getPaymentStatus(enrollment);
  const remaining = getRemainingBalance(enrollment);
  const effectiveFee = getEffectiveCourseFee(enrollment);
  const discount = getDiscountAmount(enrollment);

  return (
    <div className={compact ? '' : 'space-y-1'}>
      <p className="text-sm font-bold text-designer-dark tabular-nums">
        {formatCourseFee(amountPaid)}
      </p>
      {!compact && (
        <p className="text-xs text-gray-400 tabular-nums">
          of {formatCourseFee(effectiveFee)}
          {discount > 0 && (
            <span className="line-through ml-1 text-gray-300">
              {formatCourseFee(CREATIVE_SAMURAI_COURSE_FEE)}
            </span>
          )}
        </p>
      )}
      <DiscountBadge enrollment={enrollment} />
      {status === 'partial' && (
        <p className="text-xs font-medium text-blue-600 tabular-nums">
          {formatCourseFee(remaining)} due
        </p>
      )}
    </div>
  );
};

const EnrollmentActions = ({
  enrollment,
  processingEnrollId,
  onRecordPayment,
  onPrintIdCard,
  onDelete,
  layout = 'row',
}) => {
  const isProcessing = processingEnrollId === enrollment.id;
  const fullyPaid = isFullyPaid(enrollment);

  return (
    <div
      className={
        layout === 'row'
          ? 'flex items-center justify-end gap-2 flex-wrap'
          : 'flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100'
      }
    >
      {fullyPaid ? (
        <button
          type="button"
          onClick={() => onPrintIdCard(enrollment)}
          disabled={isProcessing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 whitespace-nowrap bg-designer-primary text-white hover:bg-red-600"
          title="Print student ID card"
        >
          <FaIdCard className="w-3.5 h-3.5" />
          Print ID Card
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onRecordPayment(enrollment)}
          disabled={isProcessing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all disabled:opacity-50 whitespace-nowrap bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
        >
          {isProcessing ? (
            <FaSpinner className="animate-spin" />
          ) : (
            <>
              <FaMoneyBillWave className="w-3.5 h-3.5" />
              Record Payment
            </>
          )}
        </button>
      )}
      <button
        type="button"
        onClick={() => onDelete(enrollment.id)}
        disabled={isProcessing}
        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-200 transition-all disabled:opacity-50"
        title="Delete enrollment"
      >
        {isProcessing ? (
          <FaSpinner className="animate-spin w-3.5 h-3.5" />
        ) : (
          <FaTrash className="w-3.5 h-3.5" />
        )}
      </button>
    </div>
  );
};

const getBorderClass = (enrollment) => {
  const status = getPaymentStatus(enrollment);
  if (status === 'paid') return 'border-green-100';
  if (status === 'partial') return 'border-blue-100';
  return 'border-amber-100';
};

const getAccentClass = (enrollment) => {
  const status = getPaymentStatus(enrollment);
  if (status === 'paid') return 'bg-green-400';
  if (status === 'partial') return 'bg-blue-400';
  return 'bg-amber-400';
};

const EnrollmentCard = ({
  enrollment,
  processingEnrollId,
  onRecordPayment,
  onPrintIdCard,
  onDelete,
}) => (
  <article
    className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all hover:shadow-md ${getBorderClass(
      enrollment
    )} ${processingEnrollId === enrollment.id ? 'opacity-50 pointer-events-none' : ''}`}
  >
    <div className={`h-1 ${getAccentClass(enrollment)}`} />
    <div className="p-4 sm:p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="inline-block font-mono text-xs font-semibold bg-gray-100 text-designer-dark px-2 py-1 rounded">
            {enrollment.student_id || '-'}
          </span>
          <div className="mt-3">
            <NameContact enrollment={enrollment} nameClass="font-semibold text-designer-dark text-base" />
          </div>
        </div>
        <PaymentBadge enrollment={enrollment} />
      </div>

      <div className="rounded-xl bg-gray-50 border border-gray-100 p-3">
        <p className="text-xs text-gray-500 font-medium mb-1">Payment</p>
        <PaidAmountDisplay enrollment={enrollment} />
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-50 text-designer-primary text-xs font-medium">
          <FaGraduationCap className="w-3 h-3" />
          {enrollment.level}
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-100 text-gray-600 text-xs font-medium">
          <FaBullseye className="w-3 h-3" />
          {enrollment.goal}
        </span>
      </div>

      <div className="flex items-start gap-1.5 text-xs text-gray-400">
        <FaCalendarAlt className="w-3 h-3 mt-0.5 shrink-0" />
        <div>
          <p className="text-xs text-gray-500 font-medium mb-0.5">Enrolled</p>
          <EnrolledAt dateString={enrollment.enrolled_at} textClass="text-xs text-gray-600" />
        </div>
      </div>

      <EnrollmentActions
        enrollment={enrollment}
        processingEnrollId={processingEnrollId}
        onRecordPayment={onRecordPayment}
        onPrintIdCard={onPrintIdCard}
        onDelete={onDelete}
        layout="stack"
      />
    </div>
  </article>
);

const PRINT_ROWS_PER_PAGE = 8;

const chunkArray = (items, size) => {
  if (!items.length) return [];
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

const PrintPageFooter = ({ page, totalPages, recordStart, recordEnd, totalRecords, showRecordRange = true }) => (
  <footer className="print-page-footer text-black">
    <span>Page {page} of {totalPages}</span>
    {showRecordRange && totalRecords > 0 && (
      <>
        <span className="print-page-footer-sep">·</span>
        <span>Records {recordStart} to {recordEnd} of {totalRecords}</span>
      </>
    )}
    <span className="print-page-footer-sep">·</span>
    <span>Arvix Creative</span>
  </footer>
);

const PrintReportHeader = ({ printMeta, totalRecords, compact = false }) => {
  if (compact) {
    return (
      <header className="print-page-header print-page-header-compact">
        <div className="flex items-baseline justify-between gap-4">
          <p className="text-xs font-bold text-black">Course Enrollments Report</p>
          <p className="text-[9px] text-black whitespace-nowrap">{totalRecords} records</p>
        </div>
      </header>
    );
  }

  return (
    <header className="print-page-header">
      <h1 className="text-xl font-bold mb-0.5 text-black leading-tight">Course Enrollments Report</h1>
      <p className="text-xs text-black">Arvix Creative | Creative Samurai Program</p>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-black">
        <span>Generated: {printMeta.generatedAt}</span>
        <span>Filter: {printMeta.filterLabel}</span>
        {printMeta.searchQuery && <span>Search: &quot;{printMeta.searchQuery}&quot;</span>}
        <span>Total records: {totalRecords}</span>
      </div>
    </header>
  );
};

const PrintTableHead = () => (
  <>
    <colgroup>
      <col style={{ width: '4%' }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '22%' }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '9%' }} />
      <col style={{ width: '11%' }} />
      <col style={{ width: '9%' }} />
    </colgroup>
    <thead className="print-table-head">
    <tr className="border-b-2 border-black">
      <th className="text-left py-1.5 pr-1.5 font-bold text-black text-[10px]">#</th>
      <th className="text-left py-1.5 pr-1.5 font-bold text-black text-[10px]">Student ID</th>
      <th className="text-left py-1.5 pr-1.5 font-bold text-black text-[10px]">Name / Contact</th>
      <th className="text-right py-1.5 pr-1.5 font-bold text-black text-[10px]">List Fee</th>
      <th className="text-right py-1.5 pr-1.5 font-bold text-black text-[10px]">Discount</th>
      <th className="text-right py-1.5 pr-1.5 font-bold text-black text-[10px]">Eff. Fee</th>
      <th className="text-right py-1.5 pr-1.5 font-bold text-black text-[10px]">Paid</th>
      <th className="text-right py-1.5 pr-1.5 font-bold text-black text-[10px]">Due</th>
      <th className="text-left py-1.5 pr-1.5 font-bold text-black text-[10px]">Enrolled</th>
      <th className="text-left py-1.5 font-bold text-black text-[10px]">Status</th>
    </tr>
  </thead>
  </>
);

const PrintTableRow = ({ enrollment, rowNumber }) => {
  const { date, time } = formatEnrolledAt(enrollment.enrolled_at);
  const status = getPaymentStatus(enrollment);
  const paymentLabel = status === 'paid' ? 'Paid' : status === 'partial' ? 'Partial' : 'Unpaid';
  const discount = getDiscountAmount(enrollment);
  const effectiveFee = getEffectiveCourseFee(enrollment);
  const remaining = getRemainingBalance(enrollment);

  return (
    <tr className="print-table-row border-b border-gray-300">
      <td className="py-1 pr-1.5 text-black text-[10px] align-top">{rowNumber}</td>
      <td className="py-1 pr-1.5 font-mono text-black text-[10px] align-top">{enrollment.student_id || '-'}</td>
      <td className="py-1 pr-1.5 text-black align-top leading-tight">
        <p className="font-medium text-[10px]">{enrollment.name}</p>
        <p className="text-[9px] text-black/80">{enrollment.email}</p>
        <p className="text-[9px] text-black/80">{enrollment.phone}</p>
      </td>
      <td className="py-1 pr-1.5 text-right text-black tabular-nums text-[10px] align-top">
        {formatCourseFee(CREATIVE_SAMURAI_COURSE_FEE)}
      </td>
      <td className="py-1 pr-1.5 text-right text-black tabular-nums text-[10px] align-top">
        {discount > 0 ? `-${formatCourseFee(discount)}` : '-'}
      </td>
      <td className="py-1 pr-1.5 text-right text-black tabular-nums font-medium text-[10px] align-top">
        {formatCourseFee(effectiveFee)}
      </td>
      <td className="py-1 pr-1.5 text-right text-black tabular-nums font-medium text-[10px] align-top">
        {formatCourseFee(getAmountPaid(enrollment))}
      </td>
      <td className="py-1 pr-1.5 text-right text-black tabular-nums text-[10px] align-top">
        {remaining > 0 ? formatCourseFee(remaining) : '-'}
      </td>
      <td className="py-1 pr-1.5 text-black align-top leading-tight">
        <p className="text-[10px]">{date}</p>
        <p className="text-[9px] text-black/80">{time}</p>
      </td>
      <td className="py-1 text-black text-[10px] align-top">{paymentLabel}</td>
    </tr>
  );
};

const PrintFinancialSummary = ({ totalIncome, totalDiscount, totalExpected, totalOutstanding, totalRecords }) => (
  <div className="print-financial-summary">
    <h2 className="text-sm font-bold text-black mb-4">Financial Summary</h2>
    <div className="print-summary-grid text-xs text-black">
      <span>Total Records</span>
      <span className="text-right font-bold tabular-nums">{totalRecords}</span>
      <span>Total Income Collected</span>
      <span className="text-right font-bold tabular-nums">{formatCourseFee(totalIncome)}</span>
      <span>Total Discounts Given</span>
      <span className="text-right font-bold tabular-nums">-{formatCourseFee(totalDiscount)}</span>
      <span>Total Expected Revenue</span>
      <span className="text-right font-bold tabular-nums">{formatCourseFee(totalExpected)}</span>
      <span>Total Outstanding</span>
      <span className="text-right font-bold tabular-nums">{formatCourseFee(totalOutstanding)}</span>
    </div>
  </div>
);

const EnrollmentsPrintView = React.forwardRef(({ enrollments, printMeta }, ref) => {
  const totalIncome = getTotalIncome(enrollments);
  const totalDiscount = getTotalDiscountGiven(enrollments);
  const totalExpected = enrollments.reduce(
    (sum, e) => sum + getEffectiveCourseFee(e),
    0
  );
  const totalOutstanding = enrollments.reduce(
    (sum, e) => sum + getRemainingBalance(e),
    0
  );

  const dataPages = chunkArray(enrollments, PRINT_ROWS_PER_PAGE);
  const hasSummaryPage = enrollments.length > 0;
  const totalPages = dataPages.length + (hasSummaryPage ? 1 : 0);

  return (
    <div ref={ref} className="enrollments-print-root bg-white text-black">
      {enrollments.length === 0 ? (
        <div className="enrollments-print-page">
          <p className="text-sm text-black py-8 text-center">No enrollments to print.</p>
        </div>
      ) : (
        <>
          {dataPages.map((pageRows, pageIndex) => {
            const recordStart = pageIndex * PRINT_ROWS_PER_PAGE + 1;
            const recordEnd = recordStart + pageRows.length - 1;

            return (
              <div key={`data-${pageIndex}`} className="enrollments-print-page">
                <PrintReportHeader
                  printMeta={printMeta}
                  totalRecords={enrollments.length}
                  compact={pageIndex > 0}
                />

                <div className="print-page-content">
                  <table className="w-full border-collapse text-black print-data-table">
                    <PrintTableHead />
                    <tbody>
                      {pageRows.map((enrollment, rowIndex) => (
                        <PrintTableRow
                          key={enrollment.id}
                          enrollment={enrollment}
                          rowNumber={recordStart + rowIndex}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                <PrintPageFooter
                  page={pageIndex + 1}
                  totalPages={totalPages}
                  recordStart={recordStart}
                  recordEnd={recordEnd}
                  totalRecords={enrollments.length}
                />
              </div>
            );
          })}

          <div className="enrollments-print-page enrollments-print-summary-page">
            <PrintReportHeader
              printMeta={printMeta}
              totalRecords={enrollments.length}
              compact
            />

            <div className="print-page-content">
              <PrintFinancialSummary
                totalIncome={totalIncome}
                totalDiscount={totalDiscount}
                totalExpected={totalExpected}
                totalOutstanding={totalOutstanding}
                totalRecords={enrollments.length}
              />
            </div>

            <PrintPageFooter
              page={totalPages}
              totalPages={totalPages}
              recordStart={0}
              recordEnd={0}
              totalRecords={enrollments.length}
              showRecordRange={false}
            />
          </div>
        </>
      )}
    </div>
  );
});

EnrollmentsPrintView.displayName = 'EnrollmentsPrintView';

const EnrollmentsTable = ({
  enrollments,
  allEnrollments,
  processingEnrollId,
  onRecordPayment,
  onPrintIdCard,
  onDelete,
  printMeta,
  printRef,
}) => (
  <>
    <div className="grid gap-4 md:grid-cols-2 xl:hidden">
      {enrollments.map((enrollment) => (
        <EnrollmentCard
          key={enrollment.id}
          enrollment={enrollment}
          processingEnrollId={processingEnrollId}
          onRecordPayment={onRecordPayment}
          onPrintIdCard={onPrintIdCard}
          onDelete={onDelete}
        />
      ))}
    </div>

    <div className="hidden xl:block bg-white rounded-xl border border-gray-200/80 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead>
            <tr className="bg-gray-50/80 border-b border-gray-200/80">
              <th className="text-left px-4 lg:px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Student ID
              </th>
              <th className="text-left px-4 lg:px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Name / Contact
              </th>
              <th className="text-left px-4 lg:px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Enrolled
              </th>
              <th className="hidden 2xl:table-cell text-left px-4 lg:px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Discount
              </th>
              <th className="text-left px-4 lg:px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                Payment
              </th>
              <th className="text-left px-4 lg:px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="text-right px-4 lg:px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {enrollments.map((enrollment, index) => {
              const status = getPaymentStatus(enrollment);
              const borderColor =
                status === 'paid'
                  ? 'border-green-400'
                  : status === 'partial'
                    ? 'border-blue-400'
                    : 'border-amber-400';
              const discount = getDiscountAmount(enrollment);

              return (
                <tr
                  key={enrollment.id}
                  className={`transition-colors hover:bg-red-50/30 ${
                    index % 2 === 1 ? 'bg-gray-50/40' : 'bg-white'
                  } ${processingEnrollId === enrollment.id ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  <td className="px-4 lg:px-5 py-4">
                    <div className={`pl-3 border-l-[3px] ${borderColor}`}>
                      <span className="font-mono text-xs font-semibold bg-gray-100 text-designer-dark px-2 py-1 rounded">
                        {enrollment.student_id || '-'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 lg:px-5 py-4">
                    <NameContact enrollment={enrollment} />
                    <div className="mt-2.5 flex flex-wrap gap-1.5">
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-red-50 text-designer-primary text-[11px] font-medium">
                        <FaGraduationCap className="w-2.5 h-2.5" />
                        {enrollment.level}
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-lg bg-gray-100 text-gray-600 text-[11px] font-medium">
                        <FaBullseye className="w-2.5 h-2.5" />
                        {enrollment.goal}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 lg:px-5 py-4">
                    <EnrolledAt dateString={enrollment.enrolled_at} />
                  </td>
                  <td className="hidden 2xl:table-cell px-4 lg:px-5 py-4">
                    {discount > 0 ? (
                      <div className="space-y-1">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">
                          <FaTag className="w-2.5 h-2.5" />
                          -{getDiscountPercent(discount)}%
                        </span>
                        <p className="text-xs text-green-600 font-medium tabular-nums">
                          {formatCourseFee(discount)} off
                        </p>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-4 lg:px-5 py-4">
                    <PaidAmountDisplay enrollment={enrollment} />
                  </td>
                  <td className="px-4 lg:px-5 py-4">
                    <PaymentBadge enrollment={enrollment} />
                  </td>
                  <td className="px-4 lg:px-5 py-4">
                    <EnrollmentActions
                      enrollment={enrollment}
                      processingEnrollId={processingEnrollId}
                      onRecordPayment={onRecordPayment}
                      onPrintIdCard={onPrintIdCard}
                      onDelete={onDelete}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>

    <div className="hidden">
      <EnrollmentsPrintView ref={printRef} enrollments={allEnrollments} printMeta={printMeta} />
    </div>
  </>
);

export default EnrollmentsTable;
